import request from "../libs/request";

import { BuildingId } from "../types/building";
import { reservationSpaceType } from "../types/reservation";
import { GenderType } from "./iotServices";

import { ImageFileInfoEachSize } from "./talkServices";
import { SatisfactionRequest } from "./userReportServices";

export type SpaceReservationRequest = BuildingId & {
  reservationDate: string; // YYYY-MM-DD
  buildingFloorId?: string;
  sort: string;
};

export type SpaceReservationDetailRequest = {
  reservationSpaceId: string;
  reservationDate: string; // YYYY-MM-DD
};

export type Company = {
  companyId: string;
  companyName: string;
  isActive: boolean;
};

export type RentalDevice = {
  rentalDeviceId: number;
  deviceName: string;
  price: number;
  isActive: boolean;
};

export type GenderTypeWithAll = "ALL" | GenderType;

export type AvailableTime = string; // "HH:MM"

export type AvailableDays = {
  sunday: boolean;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
};

export const DEFAULT_WEEK_DAYS: AvailableDays = {
  sunday: false,
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: false,
};

export const DEFAULT_WEEKEND_DAYS: AvailableDays = {
  sunday: true,
  monday: false,
  tuesday: false,
  wednesday: false,
  thursday: false,
  friday: false,
  saturday: true,
};

export enum ReservationStatus {
  WAITING = "예약 대기",
  CONFIRMED = "예약 완료",
  CANCELED = "예약 취소",
  IN_USE = "사용 중",
  COMPLETED = "사용 완료",
  CLEANING = "정리 중",
  CONNECTION = "연결 예약",
}

export type ReservationStatusValue =
  | "WAITING"
  | "CONFIRMED"
  | "CANCELED"
  | "IN_USE"
  | "COMPLETED"
  | "CLEANING"
  | "CONNECTION";

export const StepReservationStatus = {
  WAITING: 1,
  CONFIRMED: 2,
  CANCELED: 0,
  IN_USE: 3,
  COMPLETED: 4,
  CLEANING: 4,
  CONNECTION: 4,
};

export const GetStepFromReservationStatus = (statusName: string) => {
  switch (statusName) {
    case "예약 취소":
      return 0;
    case "사용 완료":
    case "정리 중":
      return 4;
    case "사용 중":
      return 3;
    case "예약 완료":
    case "승인 완료":
      return 2;
    case "예약 대기":
    default:
      return 1;
  }
};

export type Reservation = {
  spaceReservationDetailId: string;
  reservationStatus: ReservationStatusValue;
  reservationDate: string; //"2023-07-06";
  reservationTimeStart: AvailableTime;
  reservationTimeEnd: AvailableTime;
  beforeCleanTime: AvailableTime;
  afterCleanTime: AvailableTime;
  createdBy: number;
};

export type SpaceReservationItem = BuildingId & {
  reservationSpaceId: string;
  reservationSpaceName: string;
  reservationSpaceType: reservationSpaceType;
  useAutoConfirm: boolean;
  useBlockTime: boolean;
  blockTimeStart?: string;
  blockTimeEnd?: string;
  buildingName?: string;
  buildingFloorId: string;
  buildingFloorName?: string;
  companyList?: Company[];
  availableDays: AvailableDays;
  availableGender: GenderTypeWithAll;
  availableTimeStart: AvailableTime; //MM:SS
  availableTimeEnd: AvailableTime;
  availablePeopleCountStart: number;
  availablePeopleCountEnd: number;
  usableTimeStart: number;
  usableTimeEnd: number;
  dailyLimit: number;
  contactInfo: string;
  contactNumber: string;
  reservationSpaceFeatures: string;
  reservationSpaceNote: string;
  usageTermId: number;
  term: string;
  termName: string;
  usePayment: boolean;
  defaultPrice: number;
  additionalPrice: number;
  defaultPriceDays: AvailableDays;
  defaultPriceTimeStart: AvailableTime;
  defaultPriceTimeEnd: AvailableTime;
  rentalDeviceList: RentalDevice[];
  reservationList: Reservation[];

  fileInfo: { items: ImageFileInfoEachSize[] };
  isActive: boolean;
  createdBy: number;

  isConnectedAllCompany: boolean;

  isConcurrentLimit: boolean;
  concurrentLimit: number;
  isConnected: boolean;
  isParentConcurrentLimit: boolean;
  parentConcurrentLimit: number;
};

export type CancelInfo = {
  spaceReservationDetailId: string;
  longTermReservationId: string;
  cancelType: string;
  isCancelledByAdmin: boolean;
  cancelComment: string;
  createdAt: string;
  createdBy: number;
};

export type LongTermInfo = {
  longTermReservationId: string;
  reservationSpaceId: string;
  longTermReservationDateStart: AvailableTime;
  longTermReservationDateEnd: AvailableTime;
  longTermReservationDayOfWeek: AvailableDays;
  longTermReservationTimeStart: AvailableTime;
  longTermReservationTimeEnd: AvailableTime;
  beforeCleanTime: AvailableTime;
  afterCleanTime: AvailableTime;
  createdBy: number;
  modifiedBy: number;
};

export type SatisfactionInfo = SatisfactionRequest & {
  etcSatisfactionId: number;
  createdAt: string;
  createdBy: number;
  createdByName: string;
  createdByNickName: string;
  memberProfileImg: {
    items: ImageFileInfoEachSize[];
  };
  reply?: SatisfactionInfo;

  createdByImage?: string;
  createdByThumbnail: string | null;
  rental?: string;
  serviceId?: string;
};

export type SpaceReservationDetail = {
  spaceReservationDetailId: string;
  reservationSpaceId: string;
  reservationStatus: ReservationStatusValue;
  reservationDate: string; //"2023-07-06";
  reservationTimeStart: AvailableTime;
  reservationTimeEnd: AvailableTime;
  beforeCleanTime: AvailableTime | null;
  afterCleanTime: AvailableTime | null;
  purpose: string | null;
  userCount: number;
  totalSpaceDefaultPrice: number | null;
  totalDeviceDefaultPrice: number | null;
  vatDefaultPrice: number | null;
  modifiedPrice: number;
  isSelfReservation: boolean;
  actualUserName: string;
  usedDepartment: string | null;
  userTel: string;
  rentalDeviceList: RentalDevice[];
  approvalTime: string | null;
  checkInTime: string;
  longTermReservationId: string;
  createdBy: number;
  modifiedBy: number | null;
  modifiedAt: string | null;
  actualUserId: number;
  connectedSpaceReservationId: string | null;
  spaceInfo: SpaceReservationItem;
  cancelInfo: CancelInfo | null;
  longTermInfo: LongTermInfo | null;

  createdAt: string;
  isCheckInByAdmin: boolean;
  satisfactionInfo: SatisfactionInfo | null;
  fixedPrice: number;
  categoryNameS?: string;
  categoryNameM?: string;
};

const baseUrl = "space/reservation";

export const getSpaceReservationList = (params: SpaceReservationRequest) =>
  request.get<SpaceReservationItem[]>(
    `${baseUrl}/list/${params.buildingId}/${params.reservationDate}`,
    {
      params,
    }
  );

export const getSpaceReservationDetail = (
  params: SpaceReservationDetailRequest
) =>
  request.get<SpaceReservationItem>(
    `${baseUrl}/${params.reservationSpaceId}/${params.reservationDate}`
  );

export type SpaceReservationCreateRequest = {
  reservationSpaceId: string;
  reservationDate: string; // YYYY-MM-DD
  reservationTimeStart: string; // HH:MM
  reservationTimeEnd: string; // HH:MM
  beforeCleanTime: string;
  afterCleanTime: string;
  purpose?: string;
  userCount: string;
  totalSpaceDefaultPrice: string;
  totalDeviceDefaultPrice: string;
  vatDefaultPrice: string;
  isSelfReservation: boolean;

  actualUserName?: string;
  usedDepartment?: string;
  userTel?: string;
  rentalDeviceList: number[];
};

export type SpaceReservationCreateResponse = {
  reservationSpaceId: string;
  reservationDate: string; // YYYY-MM-DD
  reservationTimeStart: string; // HH:MM
  reservationTimeEnd: string; // HH:MM
  beforeCleanTime: string;
  afterCleanTime: string | null;
  purpose: string;
  userCount: string;
  totalSpaceDefaultPrice: string;
  totalDeviceDefaultPrice: string;
  vatDefaultPrice: string;
  isSelfReservation: boolean;

  actualUserName?: string;
  usedDepartment?: string;
  userTel?: string;
  rentalDeviceList: number[];

  reservationStatus: ReservationStatusValue;
  spaceReservationDetailId: string;
  createdBy: number;
};

export const createSpaceReservation = (params: SpaceReservationCreateRequest) =>
  request.post<SpaceReservationCreateResponse>(`${baseUrl}`, params);

export const getSpaceReservationDetailById = (
  spaceReservationDetailId: string
) =>
  request.get<SpaceReservationDetail>(
    `${baseUrl}/detail/${spaceReservationDetailId}`
  );

export const patchSpaceReservationCompleted = (
  spaceReservationDetailId: string
) => request.patch(`${baseUrl}/${spaceReservationDetailId}/completed`);

export const patchSpaceReservationCheckIn = (
  spaceReservationDetailId: string
) => request.patch(`${baseUrl}/${spaceReservationDetailId}/check/in`);

export const patchSpaceReservationCancel = (spaceReservationDetailId: string) =>
  request.patch(`${baseUrl}/${spaceReservationDetailId}/cancel`);

export type SpaceReservationLimitCountResponse = {
  totalConcurrentLimitReservation: number;
};

export const getSpaceReservationLimitCount = () =>
  request.get<SpaceReservationLimitCountResponse>(`${baseUrl}/limit/count`);
